/**
 * FC-VERSION-PROVIDER — FreshCards Version Provider
 *
 * Generic version metadata source for FreshCards. Any external app
 * (Homeschool, Studio, FreshVibe, third-party) can:
 *   1. Import this module directly to get version constants
 *   2. Call https://freshcards.freshvibeapps.com/api/version at runtime
 *   3. Read /version.json (static, copied to dist/)
 *
 * This module is the SOURCE OF TRUTH. The values are read at build time
 * from canonical FreshCards sources and frozen into the build:
 *   - freshcardsVersion       ← modules/freshcards/package.json → version
 *   - freshcardsSchemaVersion ← src/core/storage.ts       → schemaVersion
 *   - freshcardsBuildHash     ← git rev-parse --short HEAD
 *   - freshcardsDeployedAt    ← Date.now() at build time
 *
 * No runtime imports of the FreshCards document model. No migrations.
 * No logic changes. This module is read-only metadata exposure.
 *
 * Generic usage:
 *   import { freshcardsVersion, getFreshcardsVersionInfo } from '@freshcards/version';
 *   const info = getFreshcardsVersionInfo();
 *   // { version: '0.8.0', schemaVersion: '5.0.0', buildHash: '...', deployedAt: ... }
 */

/**
 * The shape of the canonical FreshCards version info payload.
 * Same shape as /api/version and /version.json.
 */
export interface FreshcardsVersionInfo {
  /** FreshCards release version (semver). Source: package.json. */
  version: string;
  /** FreshCards document model schema version. Source: storage.ts. */
  schemaVersion: string;
  /** Git short hash of the build. Source: `git rev-parse --short HEAD`. */
  buildHash: string;
  /** Date.now() (ms since epoch) at the moment this bundle was built. */
  deployedAt: number;
}

// ---------------------------------------------------------------------------
// Version constants (frozen at build time via vite define injection)
// ---------------------------------------------------------------------------

// Build-time-injected via vite.config.ts define block. Same canonical
// sources as vite-plugins/version-info.ts (package.json + git + storage.ts).
// Provides single source of truth: provider.ts and dist/api/version.json
// always agree, no hand-written constants to drift.
declare const __FC_VERSION__: string;
declare const __FC_SCHEMA__: string;
declare const __FC_HASH__: string;
declare const __FC_DEPLOYED_AT__: number;

/**
 * F 423 — THESE CONSTANTS MUST NOT THROW WHERE THEY ARE NOT DEFINED.
 *
 * They are injected by vite's `define` at BUILD time. Read as bare
 * identifiers, they are a `ReferenceError` the moment the module is imported
 * anywhere the define did not run — vitest, a node script, a doc tool. And a
 * module that throws on import cannot be used by a component that wants to
 * show the build number, because every test that renders that component would
 * die on a line that has nothing to do with the test.
 *
 * `typeof X !== 'undefined'` is the one form that is safe in BOTH worlds:
 * under vite the define rewrites the identifier, so the comparison becomes
 * `typeof "2.25.0" !== "undefined"` — true, and the value is the real one;
 * without the define, `typeof` on an undeclared identifier yields "undefined"
 * and does NOT throw, so the fallback is used.
 *
 * The fallbacks are deliberately obviously-fake (`0.0.0-dev`) so a build that
 * somehow lost its define cannot pass itself off as a real release.
 */
const FC_VERSION: string = typeof __FC_VERSION__ !== 'undefined' ? __FC_VERSION__ : '0.0.0-dev';
const FC_SCHEMA: string = typeof __FC_SCHEMA__ !== 'undefined' ? __FC_SCHEMA__ : '0.0.0-dev';
const FC_HASH: string = typeof __FC_HASH__ !== 'undefined' ? __FC_HASH__ : 'dev';
const FC_DEPLOYED_AT: number = typeof __FC_DEPLOYED_AT__ !== 'undefined' ? __FC_DEPLOYED_AT__ : 0;

/**
 * The FreshCards bundle (release) version.
 * Source: package.json → "version". Update path: bump in package.json → rebuild.
 */
export const freshcardsVersion: string = FC_VERSION;

/**
 * The FreshCards document model schema version.
 * Source: src/core/storage.ts → schemaVersion field. Documents at a different
 * schemaVersion need migration (handled by the consumer, NOT by this module).
 */
export const freshcardsSchemaVersion: string = FC_SCHEMA;

/**
 * Git short hash of the build.
 * Source: `git rev-parse --short HEAD` in the avidtech6/freshcards sovereign repo.
 * 7–8 hex characters. Matches `git log --oneline` output.
 */
export const freshcardsBuildHash: string = FC_HASH;

/**
 * Date.now() (ms since epoch) at the moment this bundle was built.
 * Useful for "deployed at" displays and freshness checks.
 */
export const freshcardsDeployedAt: number = FC_DEPLOYED_AT;

// ---------------------------------------------------------------------------
// Aggregate getter
// ---------------------------------------------------------------------------

/**
 * Return the canonical FreshCards version info as a single object.
 * This is the SAME shape as the /api/version endpoint and /version.json
 * static file. Any consumer (FreshCards, Homeschool, Studio, third-party)
 * gets the same shape regardless of access method.
 */
export function getFreshcardsVersionInfo(): FreshcardsVersionInfo {
  return {
    version: freshcardsVersion,
    schemaVersion: freshcardsSchemaVersion,
    buildHash: freshcardsBuildHash,
    deployedAt: freshcardsDeployedAt,
  };
}
